"use client";

import React, { useState, useCallback, useMemo } from "react";
import { ChevronDown, X } from "lucide-react";
import { Checkbox } from "./checkbox.js";

interface GroupFilter {
  type: string;
  label: string;
  count: number;
  enabled: boolean;
}

interface SearchResultGroupSidebarProps {
  results: any[];
  onGroupToggle: (type: string, enabled: boolean) => void;
  enabledGroups: Record<string, boolean>;
  getResultIcon: (type: string) => React.ReactNode;
}

const TYPE_LABELS: Record<string, string> = {
  ticket: "Tickets",
  contact: "Contacts",
  company: "Companies",
  time_entry: "Time Entries",
  note: "Notes",
  task: "Tasks",
  message: "Messages",
  email: "Emails",
  template: "Templates",
  doc: "Documentation",
  proposal: "Proposals",
};

const TYPE_ORDER = ["ticket", "contact", "company", "task", "time_entry", "message", "email", "note", "template", "doc", "proposal"];

export function SearchResultGroupSidebar({
  results,
  onGroupToggle,
  enabledGroups,
  getResultIcon,
}: SearchResultGroupSidebarProps) {
  // Count results by type
  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    
    results.forEach(result => {
      counts[result.type] = (counts[result.type] || 0) + 1;
    });

    return counts;
  }, [results]);

  // Build group filters list
  const groupFilters = useMemo(() => {
    return TYPE_ORDER
      .filter(type => groupCounts[type] && groupCounts[type] > 0)
      .map(type => ({
        type,
        label: TYPE_LABELS[type] || type.replace("_", " "),
        count: groupCounts[type],
        enabled: enabledGroups[type] !== false, // Default to enabled
      }));
  }, [groupCounts, enabledGroups]);

  const handleToggle = useCallback(
    (type: string) => {
      onGroupToggle(type, !enabledGroups[type]);
    },
    [enabledGroups, onGroupToggle]
  );

  const enabledCount = useMemo(() => {
    return Object.values(enabledGroups).filter(Boolean).length;
  }, [enabledGroups]);

  const totalCount = useMemo(() => {
    return Object.values(groupCounts).reduce((sum, count) => sum + count, 0);
  }, [groupCounts]);

  return (
    <div className="w-56 flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto h-full flex flex-col">
      {/* Header */}
      <div className="sticky top-0 bg-gray-50 border-b border-gray-200 p-4 z-10">
        <h3 className="font-semibold text-sm text-gray-900">Result Types</h3>
        <p className="text-xs text-gray-500 mt-1">
          {enabledCount} of {groupFilters.length} shown
        </p>
      </div>

      {/* Group filters */}
      <div className="flex-1 overflow-y-auto">
        {groupFilters.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            No results to filter
          </div>
        ) : (
          <div className="space-y-1 p-2">
            {groupFilters.map((group) => (
              <label
                key={group.type}
                className="flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <Checkbox
                  checked={group.enabled}
                  onCheckedChange={() => handleToggle(group.type)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 flex-1 truncate">
                      {group.label}
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {group.count}
                    </span>
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Footer - Select all / Clear all */}
      {groupFilters.length > 0 && (
        <div className="sticky bottom-0 border-t border-gray-200 bg-gray-50 p-2 space-y-1">
          <button type="button"
            onClick={() => {
              groupFilters.forEach(group => {
                if (!enabledGroups[group.type]) {
                  onGroupToggle(group.type, true);
                }
              });
            }}
            className="w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 rounded transition-colors text-left"
          >
            Select all
          </button>
          <button type="button"
            onClick={() => {
              groupFilters.forEach(group => {
                if (enabledGroups[group.type]) {
                  onGroupToggle(group.type, false);
                }
              });
            }}
            className="w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 rounded transition-colors text-left"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
